import { useToast } from '@ais-ui/ui-core';

import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import {
  getEmptySessions,
  getEmptyWebsockets,
  initializeWebsockets,
  Sessions,
  User,
  ValidatedSessions,
  WebSockets,
} from './createSession';
import { useQueryClient } from '@tanstack/react-query';
import { useProfile } from '@ais-ui/profile';
import { msalInstance } from './config/authConfig';
import { useReconnect } from './useReconnect';
import { AuthenticationResult } from '@azure/msal-browser/src/response/AuthenticationResult';
import { ROUTES } from '../../../../apps/ais/src/routes';

export interface UserCredentials {
  username: string;
  password: string;
}

type AuthMethod = 'credentials' | 'azure';

interface AuthProviderContextState {
  credentials?: UserCredentials;
  sessions: Sessions;
  authMethod?: AuthMethod;
  login: (sessions: ValidatedSessions, user: User) => void;
  loginWithAzure: () => Promise<AuthenticationResult | undefined>;
  logout: (reason: string) => void;
  reconnect: (server: string) => void;
  storeCredentials: (credentials: UserCredentials) => void;
  twoFactorEnabled: boolean;
  setTwoFactorEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  initiateWebsockets: () => Promise<WebSockets>;
  setSessions: React.Dispatch<React.SetStateAction<Sessions>>;
}

const AuthContext = createContext<AuthProviderContextState | undefined>(
  undefined
);

export interface AuthProviderProps {
  children: ReactNode;
  onUserLogout?: () => void;
  initialCredentials?: UserCredentials;
}

export const AuthProvider = ({
  children,
  initialCredentials,
}: AuthProviderProps) => {
  const [credentials, setCredentials] = useState<UserCredentials | undefined>(
    () => initialCredentials ?? readFromLocalStorage()
  );
  const [authMethod, setAuthMethod] = useState<AuthMethod>('credentials');
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);

  useEffect(() => {
    if (!msalInstance) {
      return;
    }
    msalInstance.initialize().catch((error) => {
      console.error('Failed to initialize MSAL:', error);
      Sentry.captureException(error);
    });
  }, []);

  const { setEmail, setUsername, setGroups, setUserId } = useProfile();
  const [webSockets, setWebSockets] = useState<WebSockets>(
    getEmptyWebsockets()
  );
  const [sessions, setSessions] = useState<Sessions>(getEmptySessions());
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const storeCredentials = useCallback(
    (credentials: UserCredentials) => {
      setCredentials(credentials);
      storeInLocalStorage(credentials);
    },
    [setCredentials]
  );

  const logout = useCallback(
    (reason = '') => {
      if (authMethod === 'azure' && msalInstance) {
        msalInstance.logoutPopup().catch(console.error);
        msalInstance.clearCache();
      }
      setCredentials(undefined);
      setSessions(getEmptySessions());
      setGroups([]);
      queryClient.clear();
      Sentry.setUser(null);
      navigate('/'); // Navigate to login page after logout

      if (typeof reason === 'string' && reason) {
        toast({
          className: 'logout-success-toast',
          title: 'Logged out',
          description: reason,
        });
      }
    },
    [authMethod, navigate, queryClient, setGroups, toast]
  );

  const reconnect = useReconnect(sessions, setSessions, toast, logout);

  const login = useCallback(
    (sessions: ValidatedSessions, user: User) => {
      setSessions(sessions);

      setGroups(user.groups);
      setEmail(user.email);
      setUsername(user.username);
      setUserId(user.userId);
      Sentry.setUser({
        id: user.userId,
        username: user.username,
        email: user.email,
        groups: user.groups,
        sessionId: sessions?.sessionId,
      });
      navigate(ROUTES.MAP.path);
    },
    [navigate, setEmail, setGroups, setUserId, setUsername]
  );

  const loginWithAzure = useCallback(async () => {
    try {
      if (!msalInstance) {
        throw new Error('MSAL not initialized');
      }

      const response = await msalInstance.loginPopup({
        scopes: ['https://graph.microsoft.com/.default'],
      });
      setAuthMethod('azure');
      // We had an issue, where the user had cached login from credentials login from the past, and then
      //  kept these credentials with SSO login, which means that the user is logged in as two different users
      removeFromLocalStorage();
      return response;
    } catch (error) {
      console.error('Azure login failed:', error);
      toast({
        title: 'Login Failed',
        description: 'Failed to login with Azure',
        variant: 'destructive',
      });
    }
  }, [toast]);

  const initiateWebsockets = useCallback(async (): Promise<WebSockets> => {
    if (webSockets.olServerAndClient) {
      return webSockets; // Return early if websockets already exist, as we want to reuse existing connections
    }

    const initiatedWebsockets = await initializeWebsockets();

    setWebSockets(initiatedWebsockets);

    return initiatedWebsockets;
  }, [webSockets]);

  return (
    <AuthContext.Provider
      value={{
        credentials,
        sessions,
        reconnect,
        login,
        loginWithAzure,
        logout,
        authMethod,
        storeCredentials,
        twoFactorEnabled,
        setTwoFactorEnabled,
        initiateWebsockets,
        setSessions,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

const readFromLocalStorage = () => {
  const tempCredentials = localStorage.getItem('credentials');
  return tempCredentials != null ? JSON.parse(tempCredentials) : undefined;
};

const storeInLocalStorage = (credentials: UserCredentials) => {
  localStorage.setItem('credentials', JSON.stringify(credentials));
};

const removeFromLocalStorage = () => {
  localStorage.removeItem('credentials');
};

export const useAuth = () => {
  const authContext = useContext(AuthContext);

  if (!authContext) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return authContext;
};

export const useCredentials = () => {
  const authContext = useAuth();

  const { credentials } = authContext;

  // Outcommented for now, due to SSO CLASSIC-8788
  // if (credentials == null) {
  //   throw new Error('useCredentials must be used after user is logged in');
  // }

  return credentials;
};
